import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, ShieldCheck } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState, DetailDrawer, Field, formatDateTime } from "@/components/admin/ui";
import { WARRANTY_STATUS_META } from "./supportMeta";
import { CustomerPickerField } from "./CustomerPickerField";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const inputClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";
const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";
const outlineBtn = "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10";
const dangerBtn = "inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-3.5 py-2 text-xs font-bold text-red-600 transition-colors duration-150 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-500/30 dark:text-red-400";

const NewWarrantyModal = ({ API, onClose, onCreated }) => {
  const [customer, setCustomer] = useState(null);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [productName, setProductName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!customer || !subject.trim()) return;
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/org/support/warranty`, { customer_id: customer.customer_id, subject: subject.trim(), description: description.trim(), product_or_service_name: productName.trim() || null }, { withCredentials: true });
      onCreated();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <DetailDrawer open onClose={onClose} title="New Warranty Claim" testId="new-warranty-drawer">
      <div className="space-y-4" data-testid="new-warranty-form">
        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="new-warranty-error">{error}</p>}
        <div><label className={labelClass}>Customer</label><div className="mt-1"><CustomerPickerField API={API} value={customer} onChange={setCustomer} testId="new-warranty-customer-picker" /></div></div>
        <div><label className={labelClass}>Subject</label><input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="new-warranty-subject" className={inputClass} /></div>
        <div><label className={labelClass}>Product or Service</label><input value={productName} onChange={(e) => setProductName(e.target.value)} data-testid="new-warranty-product" className={inputClass} /></div>
        <div><label className={labelClass}>Description</label><textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="new-warranty-description" className={inputClass} /></div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="new-warranty-cancel" className={outlineBtn}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving || !customer || !subject.trim()} data-testid="new-warranty-submit" className={btn}>Submit Claim</button>
        </div>
      </div>
    </DetailDrawer>
  );
};

const WarrantyDetailDrawer = ({ API, claimId, canManage, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/org/support/warranty/${claimId}`, { withCredentials: true }).then(({ data }) => setData(data)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, claimId]);
  useEffect(() => { load(); }, [load]);

  const decide = async (decision) => {
    if (!reason.trim()) { setError("A reason is required to approve or deny a claim."); return; }
    setBusy(true); setError("");
    try { await axios.patch(`${API}/org/support/warranty/${claimId}/decision`, { decision, reason: reason.trim() }, { withCredentials: true }); load(); onChanged?.(); } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const moveToReview = async () => {
    setBusy(true);
    try { await axios.patch(`${API}/org/support/warranty/${claimId}/status`, { status: "in_review" }, { withCredentials: true }); load(); onChanged?.(); } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!data && !error) return <DetailDrawer open onClose={onClose} title="Loading…" testId="warranty-detail-drawer"><p className="text-sm text-slate-400">Loading…</p></DetailDrawer>;
  if (error && !data) return <DetailDrawer open onClose={onClose} title="Warranty Claim" testId="warranty-detail-drawer"><p className="text-sm font-semibold text-red-600">{error}</p></DetailDrawer>;

  const c = data.claim;
  const decided = c.status === "approved" || c.status === "denied";

  return (
    <DetailDrawer open onClose={onClose} title={c.display_id} subtitle={c.subject} testId="warranty-detail-drawer">
      <div className="space-y-5" data-testid="warranty-detail">
        {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="warranty-detail-action-error">{error}</p>}
        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${WARRANTY_STATUS_META[c.status].cls}`}>{WARRANTY_STATUS_META[c.status].label}</span>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Customer" value={c.customer_name} testId="warranty-detail-customer" />
          <Field label="Product / Service" value={c.product_or_service_name} testId="warranty-detail-product" />
          <Field label="Submitted" value={formatDateTime(c.created_at)} testId="warranty-detail-created" />
          {decided && <Field label="Decision" value={c.decision_reason} testId="warranty-detail-decision" />}
        </div>
        <div><p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Description</p><p className="mt-1 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{c.description || "—"}</p></div>

        {canManage && !decided && (
          <div className="space-y-3 rounded-xl border border-dashed border-slate-300 p-4 dark:border-white/20" data-testid="warranty-decision-form">
            {c.status === "submitted" && <button type="button" onClick={moveToReview} disabled={busy} data-testid="warranty-move-review" className={outlineBtn}>Move to In Review</button>}
            <div><label className={labelClass}>Decision Reason</label><textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="warranty-decision-reason" className={inputClass} /></div>
            <div className="flex gap-2">
              <button type="button" onClick={() => decide("approved")} disabled={busy} data-testid="warranty-approve-btn" className={btn}>Approve</button>
              <button type="button" onClick={() => decide("denied")} disabled={busy} data-testid="warranty-deny-btn" className={dangerBtn}>Deny</button>
            </div>
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Activity</p>
          <div className="space-y-3" data-testid="warranty-detail-activity">
            {data.activity.map((a) => (
              <div key={a.event_id} className="flex items-start gap-2.5 text-sm">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0052FF]" />
                <div><p className="font-semibold text-slate-900 dark:text-white">{a.label}</p><p className="text-xs text-slate-400">{a.actor_name} · {formatDateTime(a.created_at)}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DetailDrawer>
  );
};

export const WarrantyTab = ({ API, canManage, refreshKey }) => {
  const [claims, setClaims] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/org/support/warranty`, { params: status ? { status } : {}, withCredentials: true }).then(({ data }) => setClaims(data.claims)).catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, status]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (error) return <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="support-warranty-error">{error}</p>;

  return (
    <>
      <Panel title="Warranty Claims" subtitle="Review and decide on customer warranty claims." testId="support-warranty-tab"
        action={canManage && <button type="button" onClick={() => setCreating(true)} data-testid="support-warranty-new-btn" className="flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white hover:bg-[#0040CC]"><Plus size={14} /> New Claim</button>}>
        <div className="mb-4 flex gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="support-warranty-filter-status" className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold dark:border-white/15 dark:bg-[#0B1220] dark:text-white">
            <option value="">All Statuses</option>
            {Object.keys(WARRANTY_STATUS_META).map((s) => <option key={s} value={s}>{WARRANTY_STATUS_META[s].label}</option>)}
          </select>
        </div>
        {!claims ? <p className="text-sm text-slate-400">Loading…</p> : claims.length === 0 ? (
          <EmptyState icon={ShieldCheck} title="No warranty claims yet." message="Claims submitted by customers or logged by staff will show up here." testId="support-warranty-empty" />
        ) : (
          <div className="space-y-2" data-testid="support-warranty-list">
            {claims.map((c) => (
              <button key={c.claim_id} type="button" onClick={() => setSelectedId(c.claim_id)} data-testid={`support-warranty-row-${c.claim_id}`} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 text-left transition-colors duration-150 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5">
                <div className="min-w-0"><p className="truncate text-sm font-bold text-slate-900 dark:text-white">{c.subject}</p><p className="text-xs text-slate-500 dark:text-slate-400">{c.customer_name} · {c.display_id}</p></div>
                <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold ${WARRANTY_STATUS_META[c.status].cls}`}>{WARRANTY_STATUS_META[c.status].label}</span>
              </button>
            ))}
          </div>
        )}
      </Panel>
      {creating && <NewWarrantyModal API={API} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {selectedId && <WarrantyDetailDrawer API={API} claimId={selectedId} canManage={canManage} onClose={() => setSelectedId(null)} onChanged={load} />}
    </>
  );
};
